import { DestroyRef, effect, ElementRef, inject, NgZone, Signal } from '@angular/core';
import Highcharts, {
    Chart,
    PointOptionsObject,
    TooltipFormatterContextObject,
    YAxisOptions,
} from 'highcharts/highstock';
import moment from 'moment';
import { Theme } from '../../../ui/styles/theme';
import { createTimeseriesChartOptions } from '../../proactive-recommendations/timeseries-chart/timeseries-chart.component';

/**
 * Pure chart-options builders for the recommendations-container plugins.
 *
 * Every function here is a deterministic `(input) → Highcharts.Options` (or
 * Highcharts sub-options) — no DI, no permission signals, no async work.
 * `RecommendationProcessingService` calls into these to compose its render
 * info; the dialog/card plugin components mount the resulting options
 * directly via `new Chart(...)`.
 *
 * Layering:
 *   - `createTimeseriesChartOptions` (in timeseries-chart.component.ts) is the
 *     shared low-level helper that produces a tooltip/axis/legend skeleton.
 *   - `buildEoneChartOptions` here wraps that skeleton with E//One dialog
 *     specifics (series-split at asOfTime, boundary plot-line, height,
 *     background, y-axis min/max/title).
 *   - `buildEoneCapacityExpansionChartOptions` and
 *     `buildEoneUpgradeTierChartOptions` are the per-recommendation builders
 *     that wire in the relevant series template, plot lines, tooltip, and
 *     y-axis bounds.
 *   - `buildEoneCapacityExpansionSparklineOptions` produces the small card
 *     sparkline — it does NOT share infrastructure with the dialog chart
 *     because the visual contract is intentionally different (no axes, pills,
 *     triangle marker, fixed dimensions).
 *
 * All colours come from `Theme.recommendations.*`. Sizes and z-indexes are
 * captured as constants at the top of the file rather than inline.
 */

// ─── Dialog chart layout ──────────────────────────────────────────────────
/** Height of the dialog chart, expressed as a Highcharts percent of container. */
const DIALOG_CHART_HEIGHT_PERCENT = '25%';
/** YAxis headroom above the recommended-tier plot line on the tier-upgrade chart. */
const TIER_UPGRADE_Y_AXIS_HEADROOM_MULTIPLIER = 1.15;
/**
 * Y-axis extends this fraction of the current reserve BELOW the current
 * reserve line (e.g. 0.15 → 15 %). Keeps the current line off the bottom
 * edge and gives the data line room to dip without being clipped.
 */
const CAPACITY_EXPANSION_Y_AXIS_BELOW_CURRENT_RATIO = 0.15;
/**
 * Y-axis extends this fraction of the recommended reserve ABOVE the
 * recommended reserve line. Keeps the recommended line off the top edge
 * and gives the projected data line room to grow toward / past the line.
 */
const CAPACITY_EXPANSION_Y_AXIS_ABOVE_RECOMMENDED_RATIO = 0.15;
/** Load (% of current tier) at which the dialog chart switches to warning colouring. */
const LOAD_WARNING_THRESHOLD_PERCENT = 100;

// ─── Sparkline layout ─────────────────────────────────────────────────────
/** Sparkline chart height in px — must match the card's sparkline-container SCSS. */
const SPARKLINE_HEIGHT_PX = 104.5;
/**
 * [top, right, bottom, left] in px. The right buffer keeps the end-of-
 * projection pill (centred on the rightmost data point) from being clipped;
 * the left buffer mirrors so the chart stays visually balanced. The top
 * buffer gives the projected-% pill (vertically centred on the last data
 * point) room to render without being clipped when the line is near the
 * top of the plot area.
 */
const SPARKLINE_SPACING_PX: [number, number, number, number] = [14, 28, 4, 10];
const SPARKLINE_SERIES_LINE_WIDTH_PX = 1.5;
/** Z-index for the vertical asOf plot line — sits above the area fill. */
const SPARKLINE_AS_OF_LINE_Z_INDEX = 5;
/** Z-index for the triangle marker drawn at the bottom of the asOf line. */
const SPARKLINE_AS_OF_TRIANGLE_Z_INDEX = 6;
/** Triangle marker dimensions, in px. */
const SPARKLINE_AS_OF_TRIANGLE_HALF_WIDTH_PX = 4;
const SPARKLINE_AS_OF_TRIANGLE_HEIGHT_PX = 5;
/** Y-offset (px) applied to xAxis tick labels so they sit below the plot area. */
const SPARKLINE_X_AXIS_LABEL_Y_OFFSET_PX = 18;

// ─── Pill data label ──────────────────────────────────────────────────────
const PILL_BORDER_RADIUS_PX = 10;
const PILL_PADDING_PX = 4;

// ─── Shared label / plot-line styling ─────────────────────────────────────
const CHART_LABEL_FONT_SIZE = '11px';
const PLOT_LINE_WIDTH_PX = 1;
const PLOT_LINE_Z_INDEX = 2;

/**
 * Splits a `[x, y][]` timeseries into historical/projected `PointOptionsObject[]`
 * segments around `asOfTime`. The boundary point — the first sample with
 * x > asOfTime — is duplicated into the historical segment so the solid
 * historical line and the dashed projected line join visually at that
 * boundary sample's x. This is at (or just after) asOfTime depending on
 * the sample cadence; it is not necessarily exactly asOfTime.
 *
 * Returns `boundaryX = null` when there is no projection (all points are
 * historical), so callers can decide whether to draw a boundary plot line.
 */
function splitTimeseriesAtBoundary(
    timeseries: [number, number][],
    asOfTime: number,
): {
    historicalPoints: PointOptionsObject[];
    projectedPoints: PointOptionsObject[];
    boundaryX: number | null;
} {
    const points: PointOptionsObject[] = (timeseries || []).map(([x, y]) => ({ x, y }));
    const firstProjectionIndex = points.findIndex(p => p.x > asOfTime);

    if (firstProjectionIndex < 0) {
        return { historicalPoints: points, projectedPoints: [], boundaryX: null };
    }

    const historicalPoints = points.slice(0, firstProjectionIndex);
    const projectedPoints = points.slice(firstProjectionIndex);
    historicalPoints.push(projectedPoints[0]);
    return { historicalPoints, projectedPoints, boundaryX: projectedPoints[0].x };
}

/**
 * Shared chart-options builder for E//One dialog charts.
 *
 * Returns a fully-baked Highcharts.Options that the dialog plugins can
 * mount directly via `new Chart({...options, chart: { renderTo }})`. All
 * runtime concerns are pre-resolved here:
 *   - Splits `timeseries` into historical (solid) + projected (dashed)
 *     series at `asOfTime`. The boundary point is duplicated into the
 *     historical series so the two lines join visually.
 *   - Adds a vertical xAxis plot line at the boundary x.
 *   - Wraps `createTimeseriesChartOptions` for tooltip/axis/legend defaults,
 *     then applies the E//One-specific chart height, background, and y-axis
 *     min/max/title.
 */
function buildEoneChartOptions(params: {
    timeseries: [number, number][];
    asOfTime: number;
    tooltipFormatter: (context: TooltipFormatterContextObject) => string;
    yAxisFormatter: (y: number) => string;
    plotLines: Highcharts.YAxisPlotLinesOptions[];
    seriesTemplate: Highcharts.SeriesLineOptions;
    yAxisMin: number;
    yAxisMax: number;
    yAxisTitle: string;
}): Highcharts.Options {
    const { historicalPoints, projectedPoints, boundaryX } = splitTimeseriesAtBoundary(
        params.timeseries,
        params.asOfTime,
    );

    const options = createTimeseriesChartOptions(
        params.tooltipFormatter,
        params.yAxisFormatter,
        params.plotLines,
        { ...params.seriesTemplate, data: historicalPoints },
        { ...params.seriesTemplate, dashStyle: 'Dash', data: projectedPoints },
    );

    options.chart.height = DIALOG_CHART_HEIGHT_PERCENT;
    options.chart.backgroundColor = Theme.recommendations.eoneDialogChart.background;
    const yAxis = options.yAxis as YAxisOptions;
    yAxis.min = params.yAxisMin;
    yAxis.max = params.yAxisMax;
    yAxis.title = { text: params.yAxisTitle };

    if (boundaryX !== null) {
        options.xAxis = {
            ...(options.xAxis as Highcharts.XAxisOptions),
            plotLines: [
                {
                    color: Theme.recommendations.eoneDialogChart.boundary,
                    value: boundaryX,
                    width: PLOT_LINE_WIDTH_PX,
                    zIndex: PLOT_LINE_Z_INDEX,
                },
            ],
        };
    }

    return options;
}

/**
 * Builds Highcharts options for the capacity-expansion timeseries chart.
 * Plot lines mark the current and recommended reserve levels in TiB.
 *
 * The y-axis is anchored around the meaningful range (current reserve,
 * recommended reserve, and the data line) rather than starting at 0. With
 * reserves typically in the 200–300 TiB range and only ~30 TiB between
 * current and recommended, a 0-anchored axis wastes ~80 % of the vertical
 * space and crushes the two plot lines together.
 */
export function buildEoneCapacityExpansionChartOptions(
    capacityTimeseries: [number, number][],
    currentReserveTib: number,
    recommendedReserveTib: number,
    asOfTime: number,
): Highcharts.Options {
    const lineColor = Theme.recommendations.eoneDialogChart.line;
    const warningColor = Theme.recommendations.eoneDialogChart.warning;
    const capacitySeriesTemplate: Highcharts.SeriesLineOptions = {
        name: 'Capacity',
        type: 'line',
        data: [],
        color: lineColor,
        zoneAxis: 'y',
        zones: [{ value: currentReserveTib, color: lineColor }, { color: warningColor }],
    };
    const { yAxisMin, yAxisMax } = computeCapacityExpansionYAxisBounds(
        capacityTimeseries,
        currentReserveTib,
        recommendedReserveTib,
    );
    return buildEoneChartOptions({
        timeseries: capacityTimeseries,
        asOfTime,
        tooltipFormatter: buildEoneTooltipFormatter({
            rowLabel: 'Capacity',
            valueLabel: 'TiB',
            valueFormatter: y => `${y.toFixed(1)} TiB`,
            asOfTime,
            warningThreshold: currentReserveTib,
        }),
        yAxisFormatter: y => `${y} TiB`,
        plotLines: buildEonePlotLines(
            currentReserveTib,
            recommendedReserveTib,
            'Current Reserve',
            'Recommended Reserve',
        ),
        seriesTemplate: capacitySeriesTemplate,
        yAxisMin,
        yAxisMax,
        yAxisTitle: 'Capacity (TiB)',
    });
}

/**
 * Computes the y-axis min/max for the capacity-expansion chart.
 *
 * Builds the visible window from explicit anchors:
 *   - bottom = currentReserve − 15 % of currentReserve
 *   - top    = recommendedReserve + 15 % of recommendedReserve
 *
 * Then expands to include any data outlier (line dipping below current or
 * spiking past recommended), so the data line is never clipped. Clamps the
 * lower bound at 0 (TiB can't be negative).
 */
function computeCapacityExpansionYAxisBounds(
    timeseries: [number, number][],
    currentReserveTib: number,
    recommendedReserveTib: number,
): { yAxisMin: number; yAxisMax: number } {
    const windowMin = currentReserveTib * (1 - CAPACITY_EXPANSION_Y_AXIS_BELOW_CURRENT_RATIO);
    const windowMax = recommendedReserveTib * (1 + CAPACITY_EXPANSION_Y_AXIS_ABOVE_RECOMMENDED_RATIO);

    const dataValues = timeseries.map(([, y]) => y);
    const dataMin = dataValues.length ? Math.min(...dataValues) : windowMin;
    const dataMax = dataValues.length ? Math.max(...dataValues) : windowMax;

    return {
        yAxisMin: Math.max(0, Math.min(windowMin, dataMin)),
        yAxisMax: Math.max(windowMax, dataMax),
    };
}

/**
 * Builds Highcharts options for the E//One tier upgrade load chart.
 *
 * Renders three series:
 *   1. Current tier — historical segment (solid red, up to asOfTime)
 *   2. Current tier — projected segment (dashed red, after asOfTime)
 *   3. Recommended tier — projected only (dashed green, after asOfTime)
 *
 * The Y-axis shows load as % of the respective tier's SLA. No horizontal
 * plot lines are drawn; instead the two differently-coloured series convey
 * the current vs. recommended distinction visually.
 */
export function buildEoneUpgradeTierChartOptions(
    loadTimeseries: [number, number][],
    recommendedProjectedTimeseries: [number, number][] | undefined,
    currentTierLabel: string,
    recommendedTierLabel: string,
    asOfTime: number,
): Highcharts.Options {
    const { historicalPoints, projectedPoints, boundaryX } = splitTimeseriesAtBoundary(loadTimeseries, asOfTime);
    const recommendedPoints: PointOptionsObject[] = (recommendedProjectedTimeseries || []).map(([x, y]) => ({ x, y }));
    const redColor = Theme.recommendations.eoneDialogChart.warning;
    const greenColor = Theme.recommendations.eoneDialogChart.recommendedLine;

    const currentHistoricalSeries: Highcharts.SeriesLineOptions = {
        name: 'current-historical',
        type: 'line',
        data: historicalPoints,
        color: redColor,
    };
    const currentProjectedSeries: Highcharts.SeriesLineOptions = {
        name: 'current-projected',
        type: 'line',
        data: projectedPoints,
        color: redColor,
        dashStyle: 'Dash',
    };
    const recommendedProjectedSeries: Highcharts.SeriesLineOptions = {
        name: 'recommended-projected',
        type: 'line',
        data: recommendedPoints,
        color: greenColor,
        dashStyle: 'Dash',
    };

    const { yAxisMin, yAxisMax } = computeTierUpgradeYAxisBounds(loadTimeseries, recommendedProjectedTimeseries);

    const options = createTimeseriesChartOptions(
        buildTierUpgradeTooltipFormatter(currentTierLabel, recommendedTierLabel, asOfTime),
        y => `${y}%`,
        [],
        currentHistoricalSeries,
        currentProjectedSeries,
    );

    (options.series as Highcharts.SeriesOptions[]).push(recommendedProjectedSeries);

    options.chart.height = DIALOG_CHART_HEIGHT_PERCENT;
    options.chart.backgroundColor = Theme.recommendations.eoneDialogChart.background;
    const yAxis = options.yAxis as YAxisOptions;
    yAxis.min = yAxisMin;
    yAxis.max = yAxisMax;
    yAxis.title = { text: '% of SLA' };

    if (boundaryX !== null) {
        options.xAxis = {
            ...(options.xAxis as Highcharts.XAxisOptions),
            plotLines: [
                {
                    color: Theme.recommendations.eoneDialogChart.boundary,
                    value: boundaryX,
                    width: PLOT_LINE_WIDTH_PX,
                    zIndex: PLOT_LINE_Z_INDEX,
                },
            ],
        };
    }

    return options;
}

/**
 * Computes the y-axis min/max for the tier-upgrade chart.
 *
 * Takes the maximum value across both the current and recommended timeseries,
 * applies 15 % headroom, and ensures the window never falls below 100 %
 * (the current-tier SLA baseline) so that threshold always remains visible.
 */
function computeTierUpgradeYAxisBounds(
    loadTimeseries: [number, number][],
    recommendedProjectedTimeseries: [number, number][] | undefined,
): { yAxisMin: number; yAxisMax: number } {
    const allValues = [
        ...loadTimeseries.map(([, y]) => y),
        ...(recommendedProjectedTimeseries || []).map(([, y]) => y),
    ];
    const dataMax = allValues.length ? Math.max(...allValues) : LOAD_WARNING_THRESHOLD_PERCENT;
    const windowMax = Math.max(dataMax, LOAD_WARNING_THRESHOLD_PERCENT) * TIER_UPGRADE_Y_AXIS_HEADROOM_MULTIPLIER;
    return {
        yAxisMin: 0,
        yAxisMax: windowMax,
    };
}

/**
 * Builds a Highcharts tooltip formatter for the tier-upgrade chart.
 *
 * Shows one row per active series at the hovered x, labelling each with
 * its tier name and value as "N.N%". Deduplicates the current-tier row
 * when both the historical and projected current-tier series share the
 * boundary point.
 */
function buildTierUpgradeTooltipFormatter(
    currentTierLabel: string,
    recommendedTierLabel: string,
    asOfTime: number,
): (context: TooltipFormatterContextObject) => string {
    return (context: TooltipFormatterContextObject) => {
        const x = context.x as number;
        const isProjected = x > asOfTime;
        const periodLabel = isProjected ? 'Projected' : 'Historical';
        const redColor = Theme.recommendations.eoneDialogChart.warning;
        const greenColor = Theme.recommendations.eoneDialogChart.recommendedLine;

        const seenCurrentTier = { seen: false };
        const rows: string[] = [];

        for (const pointObj of context.points ?? []) {
            const seriesName = (pointObj.series as unknown as { name: string }).name;
            const y = pointObj.point.y;

            if (seriesName === 'current-historical' || seriesName === 'current-projected') {
                if (!seenCurrentTier.seen) {
                    seenCurrentTier.seen = true;
                    rows.push(`<tr>
                        <td><span style="color:${redColor}">●</span>&nbsp;<span class="metricName">${currentTierLabel}</span></td>
                        <td><span class="metricValue metricValueRightAlign">${y.toFixed(1)}%</span></td>
                    </tr>`);
                }
            } else if (seriesName === 'recommended-projected') {
                rows.push(`<tr>
                    <td><span style="color:${greenColor}">●</span>&nbsp;<span class="metricName">${recommendedTierLabel}</span></td>
                    <td><span class="metricValue metricValueRightAlign">${y.toFixed(1)}%</span></td>
                </tr>`);
            }
        }

        return `
            <div class="perf-chart-tooltip" style="z-index: 9999">
                <div class="tt-header">
                    <div class="header-left">${moment(x).format('l')}</div>
                    <div class="header-right">${periodLabel}</div>
                </div>
                <div class="tt-body">
                    <table class="table"><tbody>${rows.join('')}</tbody></table>
                </div>
            </div>`;
    };
}

/**
 * Builds Highcharts options for the small card sparkline. Bakes in:
 *   - historical area series (Theme.recommendations.eoneCapacityExpansionSparkline.fill / line)
 *   - projected line series (Theme.recommendations.eoneCapacityExpansionSparkline.line, no fill)
 *   - vertical plotLine + triangle marker at asOfTime
 *   - hidden axes / no tooltip / no legend / no markers
 * Splits `sparklineTimeseries` at `asOfTime` and connects the historical
 * series to the boundary point so the area fills cleanly up to the asOf line.
 */
export function buildEoneCapacityExpansionSparklineOptions(
    sparklineTimeseries: [number, number][],
    asOfTime: number,
    currentUsagePercent: number,
    projectedUsagePercent: number,
    thresholdHorizonDays: number,
): Highcharts.Options | undefined {
    if (!sparklineTimeseries.length) {
        return undefined;
    }

    const firstProjectedIndex = sparklineTimeseries.findIndex(([ts]) => ts > asOfTime);
    const historicalPoints: PointOptionsObject[] =
        firstProjectedIndex < 0
            ? sparklineTimeseries.map(([x, y]) => ({ x, y }))
            : sparklineTimeseries.slice(0, firstProjectedIndex).map(([x, y]) => ({ x, y }));
    const projectedPoints: PointOptionsObject[] =
        firstProjectedIndex < 0 ? [] : sparklineTimeseries.slice(firstProjectedIndex).map(([x, y]) => ({ x, y }));
    // Connect the historical area to the boundary so the fill reaches the asOf line.
    if (firstProjectedIndex > 0 && projectedPoints.length > 0) {
        historicalPoints.push(projectedPoints[0]);
    }

    const sparkline = Theme.recommendations.eoneCapacityExpansionSparkline;

    // Two custom xAxis ticks — one labelled with the as-of date (anchored
    // at asOfTime, under the vertical "now" line), one labelled with the
    // threshold-horizon "+N days" annotation at the end of the projected line.
    const lastProjectedTimestamp =
        projectedPoints.length > 0 ? projectedPoints[projectedPoints.length - 1].x : undefined;
    const tickPositions: number[] =
        lastProjectedTimestamp !== undefined ? [asOfTime, lastProjectedTimestamp] : [asOfTime];
    const horizonLabel = `+${thresholdHorizonDays} days`;

    // Triangle marker pinned to the asOf x-pixel, drawn just below the plot
    // area. Re-rendered on every chart `redraw` so it survives resizes.
    // The triangle ELEMENT is stashed on the chart object under a private
    // key so the next redraw can dispose the previous one before drawing.
    const ASOF_TRIANGLE_KEY = '__eoneAsOfTriangle';
    const drawAsOfTriangle = (chart: Highcharts.Chart): void => {
        const stash = chart as unknown as Record<string, Highcharts.SVGElement | undefined>;
        stash[ASOF_TRIANGLE_KEY]?.destroy();
        const xAxis = chart.xAxis?.[0];
        if (!xAxis) {
            return;
        }
        const x = xAxis.toPixels(asOfTime, false);
        const yApex = chart.plotTop + chart.plotHeight;
        stash[ASOF_TRIANGLE_KEY] = chart.renderer
            .path([
                ['M', x - SPARKLINE_AS_OF_TRIANGLE_HALF_WIDTH_PX, yApex + SPARKLINE_AS_OF_TRIANGLE_HEIGHT_PX],
                ['L', x + SPARKLINE_AS_OF_TRIANGLE_HALF_WIDTH_PX, yApex + SPARKLINE_AS_OF_TRIANGLE_HEIGHT_PX],
                ['L', x, yApex],
                ['Z'],
            ] as Highcharts.SVGPathArray)
            .attr({ fill: sparkline.asOfLine, zIndex: SPARKLINE_AS_OF_TRIANGLE_Z_INDEX })
            .add();
    };

    // After Highcharts positions the pill data labels, nudge the inner <text>
    // elements upward by ~0.5 px. The SVG text baseline anchor leaves descender
    // space below the glyph, making the rendered text appear visually low inside
    // the pill background rect. This adjustment shifts only the text — the
    // background rect stays put.
    const PILL_TEXT_Y_NUDGE_PX = 0.5;
    const nudgePillText = (chart: Highcharts.Chart): void => {
        chart.series.forEach(series => {
            series.points.forEach(point => {
                const dl = (point as unknown as { dataLabel?: { text?: { element?: SVGTextElement } } }).dataLabel;
                const textEl = dl?.text?.element;
                if (!textEl) {
                    return;
                }
                // Store the original y on first render so subsequent redraws
                // always apply the nudge from the same baseline rather than
                // accumulating it on top of the already-nudged position.
                const originalY = textEl.getAttribute('data-original-y') ?? textEl.getAttribute('y') ?? '0';
                textEl.setAttribute('data-original-y', originalY);
                textEl.setAttribute('y', `${parseFloat(originalY) - PILL_TEXT_Y_NUDGE_PX}`);
            });
        });
    };

    return {
        chart: {
            height: SPARKLINE_HEIGHT_PX,
            spacing: SPARKLINE_SPACING_PX,
            backgroundColor: 'transparent',
            events: {
                load: function () {
                    drawAsOfTriangle(this);
                    nudgePillText(this);
                },
                redraw: function () {
                    drawAsOfTriangle(this);
                    nudgePillText(this);
                },
            },
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        tooltip: { enabled: false },
        xAxis: {
            type: 'datetime',
            title: { text: null },
            lineWidth: 0,
            gridLineWidth: 0,
            tickPositions,
            // No tick marks — the asOf side is marked by the plotLine +
            // triangle, the right side is just a text label.
            tickLength: 0,
            labels: {
                enabled: true,
                y: SPARKLINE_X_AXIS_LABEL_Y_OFFSET_PX,
                style: {
                    color: sparkline.currentPill,
                    fontSize: CHART_LABEL_FONT_SIZE,
                },
                formatter: function () {
                    if (this.value === asOfTime) {
                        return moment(asOfTime).format('MMM D, YYYY');
                    }
                    if (this.value === lastProjectedTimestamp) {
                        return horizonLabel;
                    }
                    return '';
                },
            },
            plotLines: [
                {
                    value: asOfTime,
                    color: sparkline.asOfLine,
                    width: PLOT_LINE_WIDTH_PX,
                    zIndex: SPARKLINE_AS_OF_LINE_Z_INDEX,
                },
            ],
        },
        yAxis: {
            labels: { enabled: false },
            title: { text: null },
            gridLineWidth: 0,
            min: 0,
            endOnTick: false,
            startOnTick: false,
        },
        plotOptions: {
            series: {
                marker: { enabled: false },
                enableMouseTracking: false,
                states: { hover: { enabled: false } },
                animation: false,
            },
        },
        series: [
            {
                name: 'Capacity',
                type: 'area',
                data: historicalPoints,
                color: sparkline.line,
                fillColor: sparkline.fill,
                fillOpacity: 1,
                lineWidth: SPARKLINE_SERIES_LINE_WIDTH_PX,
                // Pill shown on the last historical point — that's the boundary
                // point. We render the canonical `currentUsagePercent` (computed
                // from capacity_timeseries) rather than the smoothed-and-bucketed
                // sparkline value at that point, so the card pill always matches
                // the dialog's "Current usage" row.
                dataLabels: buildEoneSparklinePillDataLabel(sparkline.currentPill, currentUsagePercent),
            },
            {
                name: 'Capacity',
                type: 'line',
                data: projectedPoints,
                color: sparkline.line,
                lineWidth: SPARKLINE_SERIES_LINE_WIDTH_PX,
                // Pill shown on the last projected point — the projected
                // capacity at the threshold horizon (e.g. "157%"). Uses the
                // canonical `projectedUsagePercent` so the card pill matches
                // the dialog caption.
                dataLabels: buildEoneSparklinePillDataLabel(sparkline.projectedPill, projectedUsagePercent),
            },
        ],
    };
}

/**
 * Builds a Highcharts tooltip formatter for E//One dialog charts.
 *
 * The tooltip shows two rows: a "Historical/Projected {rowLabel}" header
 * row and a "{valueLabel}: {formatted value}" detail row. The detail
 * value turns red when it exceeds `warningThreshold`.
 */
function buildEoneTooltipFormatter(params: {
    rowLabel: string;
    valueLabel: string;
    valueFormatter: (y: number) => string;
    asOfTime: number;
    warningThreshold: number;
}): (context: TooltipFormatterContextObject) => string {
    return (context: TooltipFormatterContextObject) => {
        const pointMoment = moment(context.x);
        const isHistorical = (context.x as number) <= params.asOfTime;
        // We duplicate the boundary point into both series so the lines join
        // visually, so with shared tooltips context.points may contain two
        // identical entries at the boundary x. Render a single row block from
        // the first point.
        const value = context.points?.[0]?.point.y ?? context.y;
        const color = value > params.warningThreshold ? Theme.recommendations.eoneDialogChart.warning : 'inherit';
        const rowsHtml = `<tr>
                <td><span class="metricName">${isHistorical ? 'Historical' : 'Projected'} ${params.rowLabel}</span></td>
                <td><span class="metricValue metricValueRightAlign">&nbsp;</span></td>
            </tr>
            <tr>
                <td><span class="metricName">${params.valueLabel}</span></td>
                <td><span class="metricValue metricValueRightAlign" style="color: ${color}">${params.valueFormatter(value)}</span></td>
            </tr>`;
        return `
            <div class="perf-chart-tooltip" style="z-index: 9999">
                <div class="tt-header">
                    <div class="header-left">${pointMoment.format('l')}</div>
                    <div class="header-right">${moment.duration(pointMoment.valueOf() - moment().valueOf()).humanize(true)}</div>
                </div>
                <div class="tt-body">
                    <table class="table"><tbody>${rowsHtml}</tbody></table>
                </div>
            </div>`;
    };
}

/**
 * Builds two plot lines for an E//One dialog chart: a "current" line and
 * an optional "recommended" line drawn above it. The recommended line is
 * omitted unless `recommendedLine > currentLine`, mirroring the original
 * tier-upgrade behaviour where the recommended tier is only highlighted
 * when it sits above the current SLA.
 *
 * Colours are sourced from `Theme.recommendations.eoneDialogChart`.
 */
function buildEonePlotLines(
    currentLine: number,
    recommendedLine: number,
    currentLabel: string,
    recommendedLabel: string,
): Highcharts.YAxisPlotLinesOptions[] {
    const currentColour = Theme.recommendations.eoneDialogChart.currentPlotLine;
    const recommendedColour = Theme.recommendations.eoneDialogChart.recommendedPlotLine;
    const plotLines: Highcharts.YAxisPlotLinesOptions[] = [
        {
            value: currentLine,
            color: currentColour,
            width: PLOT_LINE_WIDTH_PX,
            dashStyle: 'Dash',
            zIndex: PLOT_LINE_Z_INDEX,
            label: {
                text: currentLabel,
                style: { color: currentColour, fontSize: CHART_LABEL_FONT_SIZE },
            },
        },
    ];

    if (recommendedLine > currentLine) {
        plotLines.push({
            value: recommendedLine,
            color: recommendedColour,
            width: PLOT_LINE_WIDTH_PX,
            dashStyle: 'Dash',
            zIndex: PLOT_LINE_Z_INDEX,
            label: {
                text: recommendedLabel,
                style: { color: recommendedColour, fontSize: CHART_LABEL_FONT_SIZE },
            },
        });
    }

    return plotLines;
}

/**
 * Pill-style Highcharts data-label rendered ONLY on the last point of the
 * series. Used for the boundary ("current") and end-of-projection
 * ("threshold") callouts on the capacity-expansion card sparkline.
 *
 * The label TEXT comes from the canonical percent value the service
 * already computed (e.g. `currentUsagePercent`, `projectedUsagePercent`)
 * rather than from the chart point's y value — that way the card pill
 * always agrees with the dialog (which reads the same canonical fields).
 * The label POSITION still comes from the chart point (last point of the
 * series) so it stays glued to the data.
 */
function buildEoneSparklinePillDataLabel(backgroundColor: string, percentValue: number): Highcharts.DataLabelsOptions {
    const label = `${percentValue}%`;
    return {
        enabled: true,
        align: 'center',
        verticalAlign: 'middle',
        crop: false,
        overflow: 'allow',
        // Highcharts binds `this` to PointLabelObject inside the formatter —
        // arrow functions would break it.
        formatter: function () {
            const isLastPoint = this.point.index === this.series.data.length - 1;
            return isLastPoint ? label : null;
        },
        backgroundColor,
        borderRadius: PILL_BORDER_RADIUS_PX,
        padding: PILL_PADDING_PX,
        style: {
            color: Theme.white,
            fontWeight: 'bold',
            fontSize: CHART_LABEL_FONT_SIZE,
            textOutline: 'none',
        },
    };
}

// ═══════════════════════════════════════════════════════════════════════════
// Highcharts mounting helper (Angular DI–aware)
// ═══════════════════════════════════════════════════════════════════════════
// Everything ABOVE this line is pure (data → options). The helper below is
// the only DI-aware export in this file — it owns the Highcharts/Angular
// lifecycle for any dialog plugin that mounts a chart.

/**
 * Mounts a Highcharts chart into a container element and keeps it in sync
 * with the supplied options. Designed to be called from a component
 * constructor (or any other injection context) — typical use:
 *
 * ```ts
 * constructor() {
 *     mountHighchartsChart(
 *         this.chartContainer,
 *         computed(() => this.renderInfo().chartOptions),
 *     );
 * }
 * ```
 *
 * Lifecycle handled here:
 *   1. Effect re-runs whenever the container or options signal changes
 *      (e.g. user toggles chart visibility, or the same component instance
 *      is reused via *ngComponentOutlet for a different recommendation).
 *      Old chart is destroyed, new one is created if container + options
 *      are both available.
 *   2. DestroyRef onDestroy tears down the chart when the host is destroyed.
 *
 * Chart construction runs outside the Angular zone so Highcharts' internal
 * requestAnimationFrame callbacks don't trigger change detection.
 */
export function mountHighchartsChart(
    container: Signal<ElementRef<HTMLElement> | undefined>,
    chartOptions: Signal<Highcharts.Options | undefined>,
): void {
    const ngZone = inject(NgZone);
    const destroyRef = inject(DestroyRef);
    let chart: Chart | undefined;

    effect(() => {
        const el = container();
        const opts = chartOptions();

        // Always destroy first — covers both "container is gone" and
        // "container is here but options changed under us".
        if (chart) {
            chart.destroy();
            chart = undefined;
        }

        if (el && opts) {
            ngZone.runOutsideAngular(() => {
                chart = new Chart({
                    ...opts,
                    chart: { ...opts.chart, renderTo: el.nativeElement },
                });
            });
        }
    });

    destroyRef.onDestroy(() => {
        chart?.destroy();
        chart = undefined;
    });
}
